import { useEffect, useRef } from 'react'
import { Box } from '@mui/material'
import { useI18n } from '@ggs/i18n'
import { Form, schema } from '@ggs/forms'
import { Country, State } from 'country-state-list'
import { isArray, split, size, noop, find, omit } from 'lodash'
import { Button } from '../../buttons'

/**
 * @typedef {import('../../../definitions').SelectOption} SelectOption
 */

/**
 * @typedef {Object} DealerFiltersProps
 * @property {function} resolveData
 */

/**
 * Dealer locator filters.
 * @param {DealerFiltersProps} props
 * @return {JSX.Element}
 */
export const DealerFilters = ({ resolveData = noop }) => {
  const { t } = useI18n()
  const countriesOptions = [
    {
      label: t('United States'),
      value: 'US',
    },
    {
      label: t('Canada'),
      value: 'CA',
    },
    {
      label: t('Saudi Arabia'),
      value: 'SA',
    },
  ]
  const defaultCountry = 'CA'
  const currentCountry = useRef(defaultCountry)

  /**
   * @typedef {Object} DealerListParams
   * @property {string} country
   * @property {string} stateProvince
   */

  /**
   * Handle dealers results filtering.
   * @param {DealerListParams} values
   * @param {any} formik
   * @return {Promise<void>}
   */
  const onSubmitHandler = async (values, { setSubmitting }) => {
    const { country, stateProvince } = values
    resolveData(
      {
        country,
        stateProvince,
      },
      setSubmitting
    )
  }

  const fieldsDefinition = {
    country: {
      label: t('Country'),
      type: schema.Forms.FieldTypes.select,
      variant: 'standard',
      options: countriesOptions,
      value: defaultCountry,
    },
    stateProvince: {
      label: t('State/Province'),
      type: schema.Forms.FieldTypes.select,
      variant: 'standard',
    },
  }

  const FormInstance = Form.useForm({
    withDraftState: false,
    formId: 'form_dealer_locator__filters',
    className: 'form_dealer_locator__filters',
    // @ts-ignore
    fields: fieldsDefinition,
    actions: [
      {
        label: t('Search'),
        variant: Button.variant.contained,
        type: 'submit',
        className: 'form__action-submit',
      },
    ],
    sx: {},
    onSubmit: onSubmitHandler,
  })

  /**
   * Compose state/province optiosn for the selected country.
   * @param {string} selectedCountry
   * @return {SelectOption[]}
   */
  const composeCountryStateOptions = (selectedCountry) => {
    const selectedCountryParts = split(selectedCountry, ',')

    // When country is a continent countries subset, country options are listed within provinces.
    if (size(selectedCountryParts) > 1) {
      const countryCountries = selectedCountryParts.map((country) => {
        const countryInfo = Country.getCountryByCode(country)
        return {
          label: countryInfo.name,
          value: country,
        }
      })

      return countryCountries
    }

    // A single country use the state/province options.
    const countryStates = State.getStatesOfCountry(selectedCountry)
    const countryStateOptions = isArray(countryStates)
      ? countryStates.map((state) => {
          return {
            label: state.name,
            value: state.isoCode,
          }
        })
      : []

    const allOption = {
      label: t('- All -'),
      value: 'all',
    }

    return [allOption, ...countryStateOptions]
  }

  /**
   * Handle update form select field options.
   */
  const updateSelectFieldOptions = () => {
    const formValues = FormInstance.context?.values || {}
    const country = formValues.country || ''
    const countryStatesOptions = composeCountryStateOptions(country)
    const selectedStateProvince = formValues.stateProvince || ''
    const existsStateProvince = find(countryStatesOptions, { value: selectedStateProvince })

    const newFields = {
      ...FormInstance.fields,
      stateProvince: {
        ...FormInstance.fields['stateProvince'],
        options: countryStatesOptions,
        disabled: false,
      },
    }

    FormInstance.setFields(newFields)

    // When country changes, reset the selected state/province.
    if (currentCountry.current !== country) {
      currentCountry.current = country
      FormInstance.context.setValues({
        ...formValues,
        stateProvince: 'all',
      })
    }
  }

  useEffect(() => {
    const country = FormInstance.context?.values?.country || ''
    if (country) {
      updateSelectFieldOptions()
    }
  }, [FormInstance.context?.values])

  return <Box className="dealer-locator__filters">{FormInstance.FormComp}</Box>
}
